import React, { useContext, useRef, useState } from 'react'
import { VideoMetaDTO } from '@west-australian-newspapers/publication-types'
import {
    AdEvent,
    CardEvents,
    CollectionEvent,
    mapListVideo,
    RenderTargetContext,
    ContentDataTypes,
    DataLoaderGlobalParams,
    CollectionDataLoaded,
} from '@news-mono/web-common'
import { MaybeLoaded } from '@west-australian-newspapers/json-react-layouts-data-loader'
import { useProduct } from '../__product'

import { VerticalVideoCarousel } from './VerticalVideoCarousel'
import {
    StyledTheNightlyVerticalVideoHubContainer,
    StyledTheNightlyVerticalVideoHubHeader,
    StyledTheNightlyVerticalVideoHubHeaderIcon,
    StyledTheNightlyVerticalVideoHubHeaderText,
    StyledVerticalVideoHub,
} from './VerticalVideoHub.styled'
import { ThemeMargins } from '../__styling'
import { VerticalVideoHubControls } from './VerticalVideoHubControls'
import { ShortsIcon } from './assets/ShortsIcon'

export interface VerticalVideoHubProps {
    videos: VideoMetaDTO[]
    onEvent: (event: CollectionEvent | CardEvents | AdEvent) => void
    adUnitPath: string
    showAds?: boolean
    currentPath: string
    publicUrl: string
    verticalGutters?: [keyof ThemeMargins, keyof ThemeMargins]
    verticalSpacing?: keyof ThemeMargins
    autoplayWhenInView?: boolean
    lazyLoad?: boolean
    loadMoreDataDefinition?: ContentDataTypes
    loadMoreInitialData?: MaybeLoaded<CollectionDataLoaded>
    loadMoreServices?: DataLoaderGlobalParams
}

export const VerticalVideoHub: React.FC<VerticalVideoHubProps> = ({
    videos,
    onEvent,
    adUnitPath,
    showAds = true,
    currentPath,
    publicUrl,
    verticalGutters,
    verticalSpacing,
    loadMoreDataDefinition,
    loadMoreInitialData,
    loadMoreServices,
}) => {
    const { renderTarget } = useContext(RenderTargetContext)
    const product = useProduct()
    const [overlayIndex, setOverlayIndex] = useState<number | null>(null)
    const initialCarouselCountRef = useRef(videos.length)
    const isTheNightly = product === 'thenightly'

    if (renderTarget === 'amp') return null
    if (!videos.length) return null

    const carouselItems = videos.slice(0, initialCarouselCountRef.current)
    const cardItems = carouselItems.map((v) =>
        mapListVideo(v, {
            publicUrl: '',
        }),
    )

    const handleSelect = (video: VideoMetaDTO) => {
        const idx = videos.findIndex((v) => v.id === video.id)
        if (idx >= 0) {
            setOverlayIndex(idx)
        }
    }

    const closeOverlay = () => setOverlayIndex(null)

    const selectedVideo = overlayIndex !== null ? videos[overlayIndex] : null

    const hubContent = (
        <StyledVerticalVideoHub
            verticalGutters={verticalGutters}
            verticalSpacing={verticalSpacing}
        >
            {overlayIndex !== null && selectedVideo && (
                <VerticalVideoHubControls
                    videos={videos}
                    startVideoIndex={overlayIndex}
                    onClose={closeOverlay}
                    onEvent={onEvent}
                    adUnitPath={adUnitPath}
                    showAds={showAds}
                    currentPath={currentPath}
                    publicUrl={publicUrl}
                    loadMoreDataDefinition={loadMoreDataDefinition}
                    loadMoreInitialData={loadMoreInitialData}
                    loadMoreServices={loadMoreServices}
                />
            )}

            <VerticalVideoCarousel
                videos={carouselItems}
                cardItems={cardItems}
                onSelect={handleSelect}
            />
        </StyledVerticalVideoHub>
    )

    if (!isTheNightly) {
        return hubContent
    }

    return (
        <StyledTheNightlyVerticalVideoHubContainer>
            <StyledTheNightlyVerticalVideoHubHeader>
                <StyledTheNightlyVerticalVideoHubHeaderIcon>
                    <ShortsIcon />
                </StyledTheNightlyVerticalVideoHubHeaderIcon>
                <StyledTheNightlyVerticalVideoHubHeaderText>
                    Shorts
                </StyledTheNightlyVerticalVideoHubHeaderText>
            </StyledTheNightlyVerticalVideoHubHeader>
            {hubContent}
        </StyledTheNightlyVerticalVideoHubContainer>
    )
}
